class CollapsibleShowMore extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-show-more-toggle]');
    if (!this.toggle) return;

    this.labelMore = this.toggle.dataset.labelMore || this.toggle.textContent.trim();
    this.labelLess = this.toggle.dataset.labelLess || '';

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.toggle.addEventListener('click', this.onToggleClick);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggleClick);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.heightAnimation?.cancel();
  }

  get collapsed() {
    return this.dataset.collapsed !== 'false';
  }

  /* Sections that want the open/close to animate name the element whose height
     should travel, e.g. data-show-more-target=".hero__text" on the SEO rich
     text. Sections without one (the FAQ list, which shows and hides whole
     items) keep the instant toggle. */
  get animationTarget() {
    const selector = this.dataset.showMoreTarget;
    return selector ? this.querySelector(selector) : null;
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onToggleClick() {
    this.setCollapsed(!this.collapsed);
  }

  /* Expand the list when a hidden question is selected in the theme editor. */
  onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    if (!event.target.classList.contains('collapsible-menu-with-image__item--extra')) return;

    this.setCollapsed(false);
  }

  setCollapsed(collapsed) {
    const target = this.animationTarget;
    const startHeight = target ? target.getBoundingClientRect().height : 0;

    this.dataset.collapsed = collapsed ? 'true' : 'false';
    this.toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');

    if (this.labelLess) {
      this.toggle.textContent = collapsed ? this.labelMore : this.labelLess;
    }

    if (target) this.animateHeight(target, startHeight);
    if (collapsed) this.keepToggleInView();
  }

  /* data-collapsed has already flipped, so the end height is whatever the CSS
     clamp just landed on - no second copy of the line count to keep in sync.
     Nothing has painted yet between the two measurements, so the jump to the
     new state is never visible; the animation covers it. */
  animateHeight(target, startHeight) {
    this.heightAnimation?.cancel();
    this.heightAnimation = null;

    if (this.prefersReducedMotion || typeof target.animate !== 'function') return;

    const endHeight = target.getBoundingClientRect().height;
    const distance = Math.abs(endHeight - startHeight);
    if (distance < 1) return;

    this.heightAnimation = target.animate(
      [{ maxHeight: `${startHeight}px` }, { maxHeight: `${endHeight}px` }],
      {
        /* Longer texts get a little more time, so a short and a long SEO text
           both read as the same gesture instead of one snapping shut. */
        duration: Math.min(600, Math.max(280, distance * 0.6)),
        easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
      }
    );
  }

  /* Closing takes hundreds of pixels out of the page above the toggle, which
     leaves the reader looking at whatever follows the section. Pull the toggle
     back once the height has settled. */
  keepToggleInView() {
    const settled = this.heightAnimation?.finished ?? Promise.resolve();

    settled
      .then(() => {
        const { top, bottom } = this.toggle.getBoundingClientRect();
        if (top >= 0 && bottom <= window.innerHeight) return;

        this.toggle.scrollIntoView({
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center',
        });
      })
      .catch(() => {
        /* A second click cancels the animation; the new one takes over. */
      });
  }
}

if (!customElements.get('collapsible-show-more')) {
  customElements.define('collapsible-show-more', CollapsibleShowMore);
}
